import { queryOptions, useMutation } from "@tanstack/react-query";
import { useFetch } from "@/lib/axios.config";
import { PaginationResponse } from "@/shared/types/api.types";
import {
  LessonItem,
  LessonDetail,
  TestDetail,
  StartTestResponse,
  TestResult,
  AnswerSubmit,
  KeywordResponse,
  LessonKeyword,
} from "./testing.types";

export enum TestingKeys {
  LessonList = "lessonList",
  LessonDetail = "lessonDetail",
  LessonKeyword = "lessonKeyword",
  TestDetail = "testDetail",
  AttemptHistory = "attemptHistory",
}

export const useLessonListOptions = (collectionId?: string | null) => {
  return queryOptions({
    queryKey: [TestingKeys.LessonList, collectionId],
    queryFn: async () => {
      const res = await useFetch.get<PaginationResponse<LessonItem>>(
        "/testing/LessonList",
        { params: { limit: 100, task_collection_id: collectionId || undefined } }
      );
      const seen = new Set<string>();
      return res.data.results.filter((lesson) => {
        if (seen.has(lesson.id)) return false;
        seen.add(lesson.id);
        return true;
      });
    },
  });
};

export const useLessonDetailOptions = (lessonId?: string) => {
  return queryOptions({
    queryKey: [TestingKeys.LessonDetail, lessonId],
    queryFn: async () => {
      const res = await useFetch.get<LessonDetail>(
        `/testing/LessonDetail/${lessonId}`
      );
      return res.data;
    },
    enabled: !!lessonId,
  });
};

export const useLessonKeywordOptions = (
  lessonId?: string,
  enabled?: boolean
) => {
  return queryOptions({
    queryKey: [TestingKeys.LessonKeyword, lessonId],
    queryFn: async () => {
      const res = await useFetch.get<LessonKeyword>(
        `/testing/LessonKeyword/${lessonId}`
      );
      return res.data;
    },
    enabled: !!lessonId && !!enabled,
    // Code is stable per user — fetch once, no refetch churn while watching.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
};

export const useTestDetailOptions = (testId?: string) => {
  return queryOptions({
    queryKey: [TestingKeys.TestDetail, testId],
    queryFn: async () => {
      const res = await useFetch.get<TestDetail>(
        `/testing/TestDetail/${testId}`
      );
      return res.data;
    },
    enabled: !!testId,
  });
};

export const useStartTestMutation = () => {
  return useMutation({
    mutationFn: async (testId: string) => {
      const res = await useFetch.post<StartTestResponse>(
        `/testing/StartTest/${testId}`
      );
      return res.data;
    },
  });
};

export const useSubmitTestMutation = () => {
  return useMutation({
    mutationFn: async ({
      testId,
      answers,
    }: {
      testId: string;
      answers: AnswerSubmit[];
    }) => {
      const res = await useFetch.post<TestResult>(
        `/testing/SubmitTest/${testId}`,
        { answers }
      );
      return res.data;
    },
  });
};

export const useVerifyKeywordMutation = () => {
  return useMutation({
    mutationFn: async ({
      lessonId,
      keyword,
    }: {
      lessonId: string;
      keyword: string;
    }) => {
      const res = await useFetch.post<KeywordResponse>(
        `/testing/VerifyKeyword/${lessonId}`,
        { keyword }
      );
      return res.data;
    },
  });
};
